/**
 * Trade Flow Map Section frontend: map SVG inject, category switching,
 * marker tooltips, and counter animation.
 */
import {
	applyMarkerLabelOffsets,
	parseMapDataset,
	renderTradeFlowsIntoSvg,
	syncActiveTradeFlowRoutes,
} from './trade-flow-geometry';

( function () {
	'use strict';

	const BLOCK_SELECTOR =
		'.wp-block-alteia-trade-flow-map-section.alteia-trade-flow-map-section';
	const ANIMATION_DURATION_MS = 1500;
	const MAP_VISIBILITY_THRESHOLD = 0.2;
	const ENTRANCE_START_PROGRESS = 0.35;
	const DEFAULT_MAP_SVG_URL =
		'/wp-content/themes/alteia/assets/src/images/trade-flow-map.svg';

	/**
	 * Whether theme entrance animation should be skipped.
	 *
	 * @return {boolean} True when counters should render in their final state.
	 */
	function shouldSkipRevealWait() {
		const reducedMotion = window.matchMedia(
			'(prefers-reduced-motion: reduce)'
		).matches;

		if ( reducedMotion ) {
			return true;
		}

		const root = document.documentElement;
		const body = document.body;

		return (
			root.classList.contains( 'no-js' ) ||
			root.classList.contains( 'alteia-no-animations' ) ||
			body.classList.contains( 'no-js' ) ||
			body.classList.contains( 'alteia-no-animations' )
		);
	}

	/**
	 * Ease-out cubic for a natural count-up feel.
	 *
	 * @param {number} t Progress from 0 to 1.
	 * @return {number} Eased progress.
	 */
	function easeOutCubic( t ) {
		return 1 - Math.pow( 1 - t, 3 );
	}

	/**
	 * Read the decimal-places attribute from a counter element.
	 *
	 * @param {HTMLElement} numberEl Counter number element.
	 * @return {number} Decimal places.
	 */
	function getCounterDecimals( numberEl ) {
		const parsed = Number.parseInt(
			numberEl.getAttribute( 'data-counter-decimals' ) || '0',
			10
		);

		return Number.isFinite( parsed ) && parsed > 0 ? parsed : 0;
	}

	/**
	 * Format a counter value, preserving the original decimal places.
	 *
	 * @param {number} value    Numeric value.
	 * @param {number} decimals Decimal places.
	 * @return {string} Display value.
	 */
	function formatCounterValue( value, decimals ) {
		if ( decimals > 0 ) {
			return value.toFixed( decimals );
		}

		return String( Math.round( value ) );
	}

	/**
	 * Animate a single counter element from 1 to its target.
	 *
	 * @param {HTMLElement} numberEl Counter number element.
	 */
	function animateCounter( numberEl ) {
		const target = Number.parseFloat(
			numberEl.getAttribute( 'data-counter-target' )
		);

		if ( ! Number.isFinite( target ) || target < 0 ) {
			return;
		}

		const decimals = getCounterDecimals( numberEl );

		if ( target === 0 ) {
			numberEl.textContent = formatCounterValue( 0, decimals );
			return;
		}

		const startValue = 1;
		const startTime = performance.now();

		/**
		 * Animation frame callback.
		 *
		 * @param {number} now Current timestamp.
		 */
		function tick( now ) {
			const elapsed = now - startTime;
			const progress = Math.min( elapsed / ANIMATION_DURATION_MS, 1 );
			const eased = easeOutCubic( progress );
			const current = startValue + ( target - startValue ) * eased;

			numberEl.textContent = formatCounterValue( current, decimals );

			if ( progress < 1 ) {
				window.requestAnimationFrame( tick );
			} else {
				numberEl.textContent = formatCounterValue( target, decimals );
			}
		}

		numberEl.textContent = formatCounterValue( startValue, decimals );
		window.requestAnimationFrame( tick );
	}

	/**
	 * Show the counter start value (1) immediately so the final number never flashes.
	 *
	 * @param {NodeList} counterCards  Counter card elements.
	 * @param {boolean}  reducedMotion Whether animation should be skipped.
	 */
	function primeCounters( counterCards, reducedMotion ) {
		counterCards.forEach( function ( card ) {
			const numberEl = card.querySelector( '[data-counter-number]' );

			if ( ! numberEl ) {
				return;
			}

			const target = Number.parseFloat(
				numberEl.getAttribute( 'data-counter-target' )
			);

			if ( ! Number.isFinite( target ) ) {
				return;
			}

			const decimals = getCounterDecimals( numberEl );

			if ( reducedMotion || target === 0 ) {
				numberEl.textContent = formatCounterValue( target, decimals );
				return;
			}

			numberEl.textContent = formatCounterValue( 1, decimals );
		} );
	}

	/**
	 * Parse the first CSS time value from a computed transition list.
	 *
	 * @param {string} value Computed style time list (e.g. "0.7s, 0.7s").
	 * @return {number} Time in milliseconds.
	 */
	function parseCssTimeMs( value ) {
		const first = String( value || '' )
			.split( ',' )[ 0 ]
			.trim();
		const amount = Number.parseFloat( first );

		if ( ! Number.isFinite( amount ) || amount < 0 ) {
			return 0;
		}

		return first.indexOf( 'ms' ) !== -1 ? amount : amount * 1000;
	}

	/**
	 * Milliseconds into a card entrance at which counters should start (~70%).
	 *
	 * Uses the element's live CSS transition duration/delay rather than a
	 * hardcoded extra timeout.
	 *
	 * @param {HTMLElement} card Counter card element.
	 * @return {number} Wait in milliseconds from `.is-animated`.
	 */
	function getEntranceStartWaitMs( card ) {
		const styles = window.getComputedStyle( card );
		const durationMs = parseCssTimeMs( styles.transitionDuration );
		const delayMs = parseCssTimeMs( styles.transitionDelay );

		return delayMs + durationMs * ENTRANCE_START_PROGRESS;
	}

	/**
	 * Start counters once the card entrance is ~70% underway.
	 *
	 * Viewport entry is owned by the theme scroll-animation system
	 * (`.is-animated`). Counters then overlap the remaining fade instead of
	 * waiting for `.anim-done`, which previously froze the number on 1.
	 *
	 * @param {NodeList|Array} cards    Counter card elements.
	 * @param {Function}       callback Called when counters may animate.
	 */
	function whenEntranceMostlyVisible( cards, callback ) {
		const cardList = Array.prototype.slice.call( cards );
		const leadCard =
			cardList.find( function ( card ) {
				return card.hasAttribute( 'data-animate' );
			} ) || null;

		if ( shouldSkipRevealWait() || ! leadCard ) {
			callback();
			return;
		}

		if ( leadCard.classList.contains( 'anim-done' ) ) {
			callback();
			return;
		}

		let finished = false;
		let startTimer = 0;
		let observer = null;

		const finish = function () {
			if ( finished ) {
				return;
			}

			finished = true;
			window.clearTimeout( startTimer );

			if ( observer ) {
				observer.disconnect();
			}

			window.requestAnimationFrame( callback );
		};

		const scheduleFromEntrance = function () {
			const waitMs = getEntranceStartWaitMs( leadCard );

			if ( waitMs <= 0 ) {
				finish();
				return;
			}

			startTimer = window.setTimeout( finish, waitMs );
		};

		if ( leadCard.classList.contains( 'is-animated' ) ) {
			finish();
			return;
		}

		observer = new window.MutationObserver( function () {
			if ( leadCard.classList.contains( 'anim-done' ) ) {
				finish();
				return;
			}

			if ( leadCard.classList.contains( 'is-animated' ) ) {
				observer.disconnect();
				scheduleFromEntrance();
			}
		} );

		observer.observe( leadCard, {
			attributes: true,
			attributeFilter: [ 'class' ],
		} );
	}

	/**
	 * Initialize counter animations for a single block instance.
	 *
	 * @param {HTMLElement} section Block root element.
	 */
	function initCounters( section ) {
		const counterCards = section.querySelectorAll( '[data-counter-card]' );

		if ( ! counterCards.length ) {
			return;
		}

		const prefersReducedMotion = window.matchMedia(
			'(prefers-reduced-motion: reduce)'
		).matches;

		primeCounters( counterCards, prefersReducedMotion );

		/**
		 * Run counters once for this block.
		 */
		function startCounters() {
			counterCards.forEach( function ( card ) {
				if ( card.dataset.counterAnimated === 'true' ) {
					return;
				}

				card.dataset.counterAnimated = 'true';

				const numberEl = card.querySelector( '[data-counter-number]' );

				if ( ! numberEl ) {
					return;
				}

				const target = Number.parseFloat(
					numberEl.getAttribute( 'data-counter-target' )
				);

				if ( ! Number.isFinite( target ) ) {
					return;
				}

				if ( prefersReducedMotion ) {
					numberEl.textContent = formatCounterValue(
						target,
						getCounterDecimals( numberEl )
					);
					return;
				}

				animateCounter( numberEl );
			} );
		}

		if ( prefersReducedMotion || shouldSkipRevealWait() ) {
			startCounters();
			return;
		}

		whenEntranceMostlyVisible( counterCards, startCounters );
	}

	/**
	 * Prepare SVG markup with unique accessibility / filter ids.
	 *
	 * @param {string} markup     Raw SVG string.
	 * @param {string} instanceId Unique instance fragment.
	 * @return {{ markup: string, filterId: string }} Prepared data.
	 */
	function prepareSvgMarkup( markup, instanceId ) {
		const safeId = String( instanceId || 'map' ).replace(
			/[^a-zA-Z0-9_-]/g,
			'-'
		);
		const filterId = 'tf-glow-' + safeId;
		const titleId = 'tf-title-' + safeId;
		const descId = 'tf-desc-' + safeId;

		return {
			filterId,
			markup: markup
				.split( 'FILTER_ID' )
				.join( filterId )
				.split( 'TITLE_ID' )
				.join( titleId )
				.split( 'DESC_ID' )
				.join( descId ),
		};
	}

	/**
	 * Inject the trade-flow SVG into an empty host.
	 *
	 * @param {HTMLElement} mapEl      Map root element.
	 * @param {string}      instanceId Unique instance id.
	 */
	function injectMapSvg( mapEl, instanceId ) {
		const host = mapEl.querySelector(
			'.alteia-trade-flow-map-section__map-svg-host'
		);

		if ( ! host || host.querySelector( 'svg' ) ) {
			return;
		}

		const svgUrl =
			mapEl.getAttribute( 'data-map-svg-url' ) || DEFAULT_MAP_SVG_URL;

		fetch( svgUrl )
			.then( function ( response ) {
				if ( ! response.ok ) {
					throw new Error( 'Failed to load map SVG' );
				}

				return response.text();
			} )
			.then( function ( markup ) {
				if ( ! host || host.querySelector( 'svg' ) ) {
					return;
				}

				const prepared = prepareSvgMarkup( markup, instanceId );
				host.innerHTML = prepared.markup;
				mapEl.style.setProperty(
					'--tf-glow-filter',
					'url(#' + prepared.filterId + ')'
				);
				host.style.setProperty(
					'--tf-glow-filter',
					'url(#' + prepared.filterId + ')'
				);

				const svg = host.querySelector( 'svg' );
				const mapData = parseMapDataset( mapEl );

				renderTradeFlowsIntoSvg(
					svg,
					mapData,
					mapEl.getAttribute( 'data-active-category' ) || ''
				);
				syncActiveTradeFlowRoutes( mapEl );
			} )
			.catch( function () {
				/* Keep host empty on failure. */
			} );
	}

	/**
	 * Pause / resume route animations via viewport visibility.
	 *
	 * @param {HTMLElement} mapEl Map root element.
	 */
	function initMapVisibility( mapEl ) {
		if ( ! ( 'IntersectionObserver' in window ) ) {
			mapEl.classList.add( 'is-map-in-view' );
			return;
		}

		const observer = new IntersectionObserver(
			function ( entries ) {
				entries.forEach( function ( entry ) {
					mapEl.classList.toggle(
						'is-map-in-view',
						entry.isIntersecting &&
							entry.intersectionRatio >= MAP_VISIBILITY_THRESHOLD
					);
				} );
			},
			{ threshold: [ 0, MAP_VISIBILITY_THRESHOLD, 0.5, 1 ] }
		);

		observer.observe( mapEl );
	}

	/**
	 * Touch-friendly marker tooltip toggle (one open at a time).
	 *
	 * @param {HTMLElement} mapEl Map root element.
	 */
	function initMarkerTooltips( mapEl ) {
		if ( mapEl.getAttribute( 'data-marker-tooltips' ) === 'false' ) {
			return;
		}
		const markersRoot = mapEl.querySelector(
			'.alteia-trade-flow-map-section__markers'
		);

		if ( ! markersRoot ) {
			return;
		}

		/**
		 * Close all open tooltips in this map.
		 */
		function closeAll() {
			markersRoot
				.querySelectorAll(
					'.alteia-trade-flow-map-section__marker.is-open'
				)
				.forEach( function ( marker ) {
					marker.classList.remove( 'is-open' );
				} );
		}

		markersRoot.addEventListener( 'click', function ( event ) {
			const marker = event.target.closest(
				'.alteia-trade-flow-map-section__marker'
			);

			if ( ! marker || ! markersRoot.contains( marker ) ) {
				return;
			}

			event.preventDefault();

			const wasOpen = marker.classList.contains( 'is-open' );
			closeAll();

			if ( ! wasOpen ) {
				marker.classList.add( 'is-open' );
			}
		} );

		document.addEventListener( 'click', function ( event ) {
			if ( ! mapEl.contains( event.target ) ) {
				closeAll();
				return;
			}

			if (
				! event.target.closest(
					'.alteia-trade-flow-map-section__marker'
				)
			) {
				closeAll();
			}
		} );
	}

	/**
	 * Apply a navigation badge's category color to the map flow animation.
	 *
	 * @param {HTMLElement|null} mapEl  Map element.
	 * @param {HTMLElement}      button Active navigation badge.
	 */
	function applyCategoryColor( mapEl, button ) {
		if ( ! mapEl || ! button ) {
			return;
		}

		const color = (
			button.style.getPropertyValue(
				'--alteia-trade-flow-map-section-nav-button-bg'
			) || ''
		).trim();

		if ( color ) {
			mapEl.style.setProperty(
				'--alteia-trade-flow-map-section-active-color',
				color
			);
		}
	}

	/**
	 * Initialize category navigation and map active category.
	 *
	 * @param {HTMLElement}      section Block root element.
	 * @param {HTMLElement|null} mapEl   Map element.
	 */
	function initNavButtons( section, mapEl ) {
		const nav = section.querySelector( '[data-trade-flow-nav]' );

		if ( ! nav ) {
			return;
		}

		const buttons = nav.querySelectorAll(
			'.alteia-trade-flow-map-section__nav-button'
		);

		if ( ! buttons.length ) {
			return;
		}

		const activeButton = nav.querySelector(
			'.alteia-trade-flow-map-section__nav-button.is-active'
		);

		applyCategoryColor( mapEl, activeButton || buttons[ 0 ] );
		syncActiveTradeFlowRoutes( mapEl );

		buttons.forEach( function ( button ) {
			button.addEventListener( 'click', function () {
				buttons.forEach( function ( item ) {
					const isActive = item === button;
					item.classList.toggle( 'is-active', isActive );
					item.setAttribute(
						'aria-pressed',
						isActive ? 'true' : 'false'
					);
				} );

				if ( mapEl && button.dataset.category ) {
					mapEl.setAttribute(
						'data-active-category',
						button.dataset.category
					);

					applyCategoryColor( mapEl, button );
					syncActiveTradeFlowRoutes( mapEl );

					mapEl
						.querySelectorAll(
							'.alteia-trade-flow-map-section__marker.is-open'
						)
						.forEach( function ( marker ) {
							marker.classList.remove( 'is-open' );
						} );
				}
			} );
		} );
	}

	/**
	 * Initialize map behaviours for a single block instance.
	 *
	 * @param {HTMLElement} section Block root element.
	 * @param {number}      index   Instance index.
	 * @return {HTMLElement|null} Map element.
	 */
	function initMap( section, index ) {
		const mapEl = section.querySelector( '[data-trade-flow-map]' );

		if ( ! mapEl ) {
			return null;
		}

		const instanceId = 'tf-' + String( index ) + '-' + String( Date.now() );

		mapEl.setAttribute( 'data-map-instance-id', instanceId );

		injectMapSvg( mapEl, instanceId );
		initMapVisibility( mapEl );
		initMarkerTooltips( mapEl );
		applyMarkerLabelOffsets( mapEl );

		return mapEl;
	}

	/**
	 * Initialize a single Trade Flow Map Section block instance.
	 *
	 * @param {HTMLElement} section Block root element.
	 * @param {number}      index   Instance index.
	 */
	function initTradeFlowMapBlock( section, index ) {
		if ( section.dataset.tradeFlowMapInitialized === 'true' ) {
			return;
		}

		section.dataset.tradeFlowMapInitialized = 'true';

		const mapEl = initMap( section, index );
		initNavButtons( section, mapEl );
		initCounters( section );
	}

	/**
	 * Initialize all block instances on the page.
	 */
	function initAllTradeFlowMapBlocks() {
		const sections = document.querySelectorAll( BLOCK_SELECTOR );

		sections.forEach( function ( section, index ) {
			initTradeFlowMapBlock( section, index );
		} );
	}

	if ( document.readyState === 'loading' ) {
		document.addEventListener(
			'DOMContentLoaded',
			initAllTradeFlowMapBlocks
		);
	} else {
		initAllTradeFlowMapBlocks();
	}
} )();
